/* global BigInt */
import { useState, useContext, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { WalletContext } from '../../../contexts/WalletContext';
import { NetworkContext } from '../../../contexts/NetworkContext';
import { SettingsContext } from '../../../contexts/SettingsContext';
import { TransactionContext, TRANSACTION_TYPES } from '../../../contexts/TransactionContext';
import { formatGasPriceForDisplay, formatGasPriceForTransaction } from '../utils/gasUtils';
import { ethers, parseEther, parseUnits, formatEther, formatUnits } from 'ethers';

/**
 * Hook to handle transaction preparation and execution
 * Manages gas settings, transaction confirmation, and execution
 */
export const useTransactions = (refreshBalance, refreshTokens) => {
  const { t } = useTranslation();
  const { currentWallet, provider } = useContext(WalletContext);
  const { currentChain } = useContext(NetworkContext);
  const { gasOption, customGasPrice, autoConfirm } = useContext(SettingsContext);
  const { addTransaction, updateTransactionStatus } = useContext(TransactionContext);

  const [selectedToken, setSelectedToken] = useState(null);
  const [selectedNFT, setSelectedNFT] = useState(null); // NEW: Add NFT state
  const [amountIn, setAmountIn] = useState('');
  const [confirmationDetails, setConfirmationDetails] = useState(null);
  const [txnHash, setTxnHash] = useState(null);
  const [tempGasOption, setTempGasOption] = useState(null);
  const [tempGasPrice, setTempGasPrice] = useState(null);
  const [dynamicGasPrice, setDynamicGasPrice] = useState(null);

  // Get the native token symbol
  const getNativeTokenSymbol = useCallback(() => {
    return currentChain && currentChain.nativeTokenName
      ? currentChain.nativeTokenName.includes('(')
        ? currentChain.nativeTokenName.split('(')[1].replace(')', '').trim()
        : currentChain.nativeTokenName
      : 'ETH';
  }, [currentChain]);

  // Fetch gas price
  const fetchGasPrice = useCallback(async () => {
    if (!provider) return;
    try {
      const feeData = await provider.getFeeData();
      const gasPriceBN = feeData.maxFeePerGas || feeData.gasPrice;
      if (gasPriceBN) {
        const gweiValue = formatUnits(gasPriceBN, 'gwei');
        setDynamicGasPrice(gweiValue);
      }
    } catch (err) {
      console.error("Error fetching fee data:", err);
    }
  }, [provider]);

  // Fetch gas price on provider or chain change
  useEffect(() => {
    fetchGasPrice();
  }, [provider, currentChain, fetchGasPrice]);

  // Get gas price based on settings
  const getGasPriceFromSettings = useCallback((specificOption, specificPrice) => {
    try {
      const useOption = specificOption || tempGasOption || gasOption;
      const usePrice = specificPrice || tempGasPrice || customGasPrice;
      
      const currentGasPrice = dynamicGasPrice ? parseFloat(dynamicGasPrice) : null;
      
      let calculatedPrice;
      if (currentGasPrice) {
        switch (useOption) {
          case 'slow':
            calculatedPrice = String(currentGasPrice * 0.8);
            break;
          case 'medium':
            calculatedPrice = String(currentGasPrice);
            break;
          case 'fast':
            calculatedPrice = String(currentGasPrice * 1.2);
            break;
          case 'custom':
            calculatedPrice = usePrice;
            break;
          default:
            calculatedPrice = usePrice;
        }
      } else {
        calculatedPrice = usePrice || "3";
      }
      
      // Format gas price for transaction submission
      const formattedPrice = formatGasPriceForTransaction(calculatedPrice);
      
      return parseUnits(formattedPrice, 'gwei');
    } catch (err) {
      console.error("Error parsing gasPrice:", err);
      return parseUnits("3", "gwei");
    }
  }, [dynamicGasPrice, tempGasOption, tempGasPrice, gasOption, customGasPrice]);

  // Prepare transaction (validate and set confirmation details)
  const prepareTransaction = useCallback(async (tokenData, amount, receiver, nativePrice = 0) => {
    if (!currentWallet) {
      alert(t('wallet.errors.noWalletConnected', 'No wallet connected!'));
      return { success: false };
    }
    
    setTempGasOption(null);
    setTempGasPrice(null);
    
    if (!ethers.isAddress(receiver)) {
      alert(t('wallet.errors.invalidAddress', 'Invalid receiver address'));
      return { success: false };
    }
  
    if (!amount || amount === '' || isNaN(parseFloat(amount))) {
      alert(t('wallet.errors.invalidAmount', 'Please enter a valid amount'));
      return { success: false };
    }
    
    setAmountIn(amount);
  
    const gasLimit = (tokenData && tokenData.address && tokenData.address.toLowerCase() !== 'native')
      ? 60000  // ERC-20 transfer
      : 21000; // Native token transfer
    
    try {
      const gasPriceBN = getGasPriceFromSettings();
      const totalFeeWei = gasPriceBN * BigInt(gasLimit);
      const totalFeeEth = formatEther(totalFeeWei);
      
      // Get USD value if available in the requested format
      let usdValuePart = "";
      if (nativePrice > 0) {
        const feeUsd = parseFloat(totalFeeEth) * nativePrice;
        if (feeUsd > 0) {
          usdValuePart = t('wallet.transactionFeeUsd', '({{amount}}$)', { amount: feeUsd.toFixed(6) });
        }
      }
      
      const nativeTokenSymbol = getNativeTokenSymbol();
      
      const detailObj = {
        type: (tokenData && tokenData.address && tokenData.address.toLowerCase() !== 'native')
          ? t('wallet.transactionTypes.tokenTransfer', 'Token Transfer')
          : t('wallet.transactionTypes.nativeTransfer', 'Native Transfer'),
        from: currentWallet.address,
        to: receiver,
        spendingAmount: `${amount} ${tokenData?.symbol || nativeTokenSymbol}`,
        gasFee: `${totalFeeEth} ${nativeTokenSymbol} ${usdValuePart}`
      };
      
      setConfirmationDetails(detailObj);
      setSelectedToken(tokenData);
      setTxnHash(null);
      
      return {
        success: true,
        autoConfirm,
        details: detailObj
      };
    } catch (err) {
      console.error("Error preparing transaction:", err);
      alert(t('wallet.errors.preparingTransaction', 'Error preparing transaction: {{message}}', { message: err.message }));
      return { success: false };
    }
  }, [currentWallet, getGasPriceFromSettings, getNativeTokenSymbol, autoConfirm, t]);

  // NEW: Prepare NFT transaction (similar to prepareTransaction but for NFTs)
  const prepareNFTTransaction = useCallback(async (nftData, receiver, nativePrice = 0) => {
    if (!currentWallet) {
      alert(t('wallet.errors.noWalletConnected', 'No wallet connected!'));
      return { success: false };
    }
    
    // Reset temporary gas settings
    setTempGasOption(null);
    setTempGasPrice(null);
    
    if (!ethers.isAddress(receiver)) {
      alert(t('wallet.errors.invalidAddress', 'Invalid receiver address'));
      return { success: false };
    }

    if (!nftData?.nft) {
      alert(t('wallet.errors.invalidNFTData', 'Invalid NFT data'));
      return { success: false };
    }

    // NFT transfers typically use around 100,000 gas
    const gasLimit = 100000;
    
    try {
      const gasPriceBN = getGasPriceFromSettings();
      const totalFeeWei = gasPriceBN * BigInt(gasLimit);
      const totalFeeEth = formatEther(totalFeeWei);
      
      // Get USD value if available
      let usdValuePart = "";
      if (nativePrice > 0) {
        const feeUsd = parseFloat(totalFeeEth) * nativePrice;
        if (feeUsd > 0) {
          usdValuePart = t('wallet.transactionFeeUsd', '({{amount}}$)', { amount: feeUsd.toFixed(6) });
        }
      }
      
      const nativeTokenSymbol = getNativeTokenSymbol();
      const nftName = nftData.nft.name || `${nftData.nft.collectionName || 'NFT'} #${nftData.nft.tokenId}`;
      
      const detailObj = {
        type: t('wallet.transactionTypes.nftTransfer', 'NFT Transfer'),
        from: currentWallet.address,
        to: receiver,
        spendingAmount: nftName,
        gasFee: `${totalFeeEth} ${nativeTokenSymbol} ${usdValuePart}`
      };
      
      setConfirmationDetails(detailObj);
      setSelectedNFT(nftData); // Set selected NFT for confirmation modal
      setTxnHash(null);
      
      return {
        success: true,
        autoConfirm,
        details: detailObj
      };
    } catch (err) {
      console.error("Error preparing NFT transaction:", err);
      alert(t('wallet.errors.preparingNFTTransaction', 'Error preparing NFT transaction: {{message}}', { message: err.message }));
      return { success: false };
    }
  }, [currentWallet, getGasPriceFromSettings, getNativeTokenSymbol, autoConfirm, t]);

  // Execute the transaction (existing function - keeping as is)
  const executeTransaction = useCallback(async (receiver, gasSettings, directAmount = null) => {
    if (!currentWallet) return { success: false, error: t('wallet.errors.noWalletConnected', 'No wallet connected') };
    
    // Use direct amount if provided (for auto-confirm), otherwise use state
    const transactionAmount = directAmount || amountIn;
    
    // Validate amount and receiver
    if (!transactionAmount || transactionAmount === '') {
      return { success: false, error: t('wallet.errors.emptyAmount', 'Transaction amount cannot be empty') };
    }
    
    if (!receiver || !ethers.isAddress(receiver)) {
      return { success: false, error: t('wallet.errors.invalidAddress', 'Invalid receiver address') };
    }
    
    try {
      const w = new ethers.Wallet(currentWallet.privateKey, provider);
      
      // Use temporary gas settings if available, otherwise use settings from context
      let effectiveGasOption = tempGasOption || gasOption;
      let effectiveGasPrice = tempGasPrice || customGasPrice;
      
      // If gas settings were passed from the confirmation modal, use those
      if (gasSettings) {
        effectiveGasOption = gasSettings.gasOption;
        effectiveGasPrice = gasSettings.gasPrice;
      }
      
      // Calculate gas price based on effective settings
      let gasPriceBN;
      if (effectiveGasOption === 'custom') {
        gasPriceBN = parseUnits(String(effectiveGasPrice), 'gwei');
      } else {
        const currentGasPrice = dynamicGasPrice ? parseFloat(dynamicGasPrice) : 3;
        let multiplier;
        switch (effectiveGasOption) {
          case 'slow': multiplier = 0.8; break;
          case 'medium': multiplier = 1.0; break;
          case 'fast': multiplier = 1.2; break;
          default: multiplier = 1.0;
        }
        const calculatedPrice = (currentGasPrice * multiplier).toFixed(9);
        gasPriceBN = parseUnits(calculatedPrice, 'gwei');
      }
      
      // Submit the transaction
      let tx;
      const nativeTokenSymbol = getNativeTokenSymbol();

      // Check if this is an ERC-20 token transfer or native token transfer
      if (selectedToken && selectedToken.address && selectedToken.address.toLowerCase() !== 'native') {
        // ERC-20 token transfer
        const erc20ABI = ["function transfer(address to, uint256 value) public returns (bool)"];
        const decimals = selectedToken.decimals || 18;
        
        // Ensure amount is a valid string and not empty
        const amountStr = String(transactionAmount).trim();
        if (!amountStr || amountStr === '') {
          throw new Error(t('wallet.errors.emptyAmount', 'Transaction amount cannot be empty'));
        }
        
        const amountBN = parseUnits(amountStr, Number(decimals));
        const contract = new ethers.Contract(selectedToken.address, erc20ABI, w);

        // Check if the chain supports EIP-1559 transactions
        if (currentChain.supportsEIP1559) {
          const feeData = await provider.getFeeData();
          if (feeData.maxFeePerGas && feeData.maxPriorityFeePerGas) {
            tx = await contract.transfer(receiver, amountBN, {
              maxFeePerGas: gasPriceBN,
              maxPriorityFeePerGas: feeData.maxPriorityFeePerGas
            });
          } else {
            tx = await contract.transfer(receiver, amountBN, { gasPrice: gasPriceBN });
          }
        } else {
          tx = await contract.transfer(receiver, amountBN, { gasPrice: gasPriceBN });
        }

        // Add the transaction to the context with 'pending' status
        addTransaction({
          hash: tx.hash,
          type: TRANSACTION_TYPES.SEND,
          from: currentWallet.address,
          to: receiver,
          value: amountStr,
          tokenSymbol: selectedToken.symbol,
          tokenDecimal: decimals,
          status: 'pending',
          network: currentChain?.key || 'unknown',
          data: tx.data || '',
          explorerUrl: currentChain?.explorerUrl ? `${currentChain.explorerUrl}/tx/${tx.hash}` : ''
        });

      } else {
        // Native token transfer
        const amountStr = String(transactionAmount).trim();
        if (!amountStr || amountStr === '') {
          throw new Error(t('wallet.errors.emptyAmount', 'Transaction amount cannot be empty'));
        }
        
        // Check if the chain supports EIP-1559 transactions
        if (currentChain.supportsEIP1559) {
          const feeData = await provider.getFeeData();
          if (feeData.maxFeePerGas && feeData.maxPriorityFeePerGas) {
            tx = await w.sendTransaction({
              to: receiver,
              value: parseEther(amountStr),
              maxFeePerGas: gasPriceBN,
              maxPriorityFeePerGas: feeData.maxPriorityFeePerGas
            });
          } else {
            tx = await w.sendTransaction({
              to: receiver,
              value: parseEther(amountStr),
              gasPrice: gasPriceBN
            });
          }
        } else {
          tx = await w.sendTransaction({
            to: receiver,
            value: parseEther(amountStr),
            gasPrice: gasPriceBN
          });
        }

        // Add the transaction to the context with 'pending' status
        addTransaction({
          hash: tx.hash,
          type: TRANSACTION_TYPES.SEND,
          from: currentWallet.address,
          to: receiver,
          value: transactionAmount,
          tokenSymbol: nativeTokenSymbol,
          tokenDecimal: 18,
          status: 'pending',
          network: currentChain?.key || 'unknown',
          data: '',
          explorerUrl: currentChain?.explorerUrl ? `${currentChain.explorerUrl}/tx/${tx.hash}` : ''
        });
      }

      // Wait for transaction confirmation
      console.log("Transaction submitted:", tx.hash);
      console.log("Waiting for confirmation...");
      
      const receipt = await tx.wait();
      console.log("Transaction confirmed!", receipt);
      
      // Update transaction status in context to 'success'
      updateTransactionStatus(tx.hash, receipt.status ? 'success' : 'failed');
      
      // Update transaction hash state
      setTxnHash(tx.hash);
      
      // Reset temporary gas settings
      setTempGasOption(null);
      setTempGasPrice(null);
      
      // Refresh balances
      if (selectedToken && selectedToken.address && selectedToken.address.toLowerCase() !== 'native') {
        refreshTokens();
      } else {
        refreshBalance();
      }
      
      return {
        success: true,
        txHash: tx.hash
      };
    } catch (err) {
      console.error("Transaction failed:", err);
      
      // If we have a transaction hash but it failed, update the status
      if (err.transaction && err.transaction.hash) {
        updateTransactionStatus(err.transaction.hash, 'failed');
      }
      
      return {
        success: false,
        error: err.message,
        txHash: err.transaction ? err.transaction.hash : null
      };
    }
  }, [
    currentWallet, provider, currentChain, amountIn, selectedToken, 
    tempGasOption, tempGasPrice, gasOption, customGasPrice,
    dynamicGasPrice, getNativeTokenSymbol, addTransaction, 
    updateTransactionStatus, refreshTokens, refreshBalance, t
  ]);

  // NEW: Execute NFT transaction with proper gas handling
  const executeNFTTransaction = useCallback(async (nftData, receiver, gasSettings = null) => {
    try {
      if (!provider || !currentWallet || !nftData.nft) {
        throw new Error(t('wallet.errors.invalidNFTData', 'Invalid NFT data'));
      }

      // Create wallet instance with private key (same pattern as regular transactions)
      const wallet = new ethers.Wallet(currentWallet.privateKey, provider);

      // ERC-721 ABI for transfers
      const erc721ABI = [
        'function safeTransferFrom(address from, address to, uint256 tokenId)',
        'function transferFrom(address from, address to, uint256 tokenId)',
        'function ownerOf(uint256 tokenId) view returns (address)',
        'function getApproved(uint256 tokenId) view returns (address)',
        'function isApprovedForAll(address owner, address operator) view returns (bool)'
      ];

      // Create contract instance with wallet
      const nftContract = new ethers.Contract(
        nftData.nft.contractAddress, 
        erc721ABI, 
        wallet
      );

      // Verify ownership before transfer
      const owner = await nftContract.ownerOf(nftData.nft.tokenId);
      if (owner.toLowerCase() !== currentWallet.address.toLowerCase()) {
        throw new Error(t('wallet.errors.nftNotOwned', 'You do not own this NFT'));
      }

      // Use the same gas calculation logic as token transfers
      let effectiveGasOption = tempGasOption || gasOption;
      let effectiveGasPrice = tempGasPrice || customGasPrice;
      
      // If gas settings were passed from the confirmation modal, use those
      if (gasSettings) {
        effectiveGasOption = gasSettings.gasOption;
        effectiveGasPrice = gasSettings.gasPrice;
      }
      
      // Calculate gas price based on effective settings (same as executeTransaction)
      let gasPriceBN;
      if (effectiveGasOption === 'custom') {
        gasPriceBN = parseUnits(String(effectiveGasPrice), 'gwei');
      } else {
        const currentGasPrice = dynamicGasPrice ? parseFloat(dynamicGasPrice) : 3;
        let multiplier;
        switch (effectiveGasOption) {
          case 'slow': multiplier = 0.8; break;
          case 'medium': multiplier = 1.0; break;
          case 'fast': multiplier = 1.2; break;
          default: multiplier = 1.0;
        }
        const calculatedPrice = (currentGasPrice * multiplier).toFixed(9);
        gasPriceBN = parseUnits(calculatedPrice, 'gwei');
      }

      // Prepare transaction options
      const txOptions = {
        gasLimit: 100000 // Standard gas limit for NFT transfers
      };

      // Handle EIP-1559 vs legacy transactions (same pattern as token transfers)
      if (currentChain?.supportsEIP1559) {
        const feeData = await provider.getFeeData();
        if (feeData.maxFeePerGas && feeData.maxPriorityFeePerGas) {
          txOptions.maxFeePerGas = gasPriceBN;
          txOptions.maxPriorityFeePerGas = feeData.maxPriorityFeePerGas;
        } else {
          txOptions.gasPrice = gasPriceBN;
        }
      } else {
        txOptions.gasPrice = gasPriceBN;
      }

      console.log('Sending NFT transfer transaction with gas settings:', {
        gasOption: effectiveGasOption,
        gasPrice: effectiveGasPrice,
        gasPriceBN: gasPriceBN.toString()
      });

      // Execute the NFT transfer using safeTransferFrom
      const tx = await nftContract.safeTransferFrom(
        currentWallet.address,
        receiver,
        nftData.nft.tokenId,
        txOptions
      );

      console.log('NFT transfer transaction sent:', tx.hash);
      
      // Add the transaction to the context with 'pending' status (same as token transfers)
      addTransaction({
        hash: tx.hash,
        type: TRANSACTION_TYPES.SEND_NFT,
        from: currentWallet.address,
        to: receiver,
        value: '1', // NFTs are non-fungible, so value is always 1
        tokenSymbol: nftData.nft.collectionName || 'NFT',
        status: 'pending',
        network: currentChain?.key || 'unknown',
        data: tx.data || '',
        explorerUrl: currentChain?.explorerUrl ? `${currentChain.explorerUrl}/tx/${tx.hash}` : null,
        nftData: nftData.nft // Store NFT data for transaction history
      });
      
      // Wait for transaction confirmation
      const receipt = await tx.wait();
      
      if (receipt.status === 1) {
        console.log('NFT transfer confirmed:', receipt);
        
        // Update transaction status to 'confirmed'
        updateTransactionStatus(tx.hash, 'confirmed');
        
        // Update transaction hash state
        setTxnHash(tx.hash);
        
        // Reset temporary gas settings
        setTempGasOption(null);
        setTempGasPrice(null);
        
        return { success: true, txHash: tx.hash };
      } else {
        // Update transaction status to 'failed'
        updateTransactionStatus(tx.hash, 'failed');
        throw new Error(t('wallet.errors.transactionFailed', 'Transaction failed'));
      }
    } catch (error) {
      console.error('Error executing NFT transaction:', error);
      
      // Provide more specific error messages
      let errorMessage = error.message;
      if (error.code === 'INSUFFICIENT_FUNDS') {
        errorMessage = t('wallet.errors.insufficientFunds', 'Insufficient funds for gas fee');
      } else if (error.code === 'USER_REJECTED') {
        errorMessage = t('wallet.errors.userRejected', 'Transaction rejected by user');
      } else if (error.reason) {
        errorMessage = error.reason;
      }

      return { success: false, error: errorMessage };
    }
  }, [
    currentWallet, provider, currentChain, tempGasOption, tempGasPrice, 
    gasOption, customGasPrice, dynamicGasPrice, addTransaction, 
    updateTransactionStatus, t
  ]);

  // Gas setting adjustment
  const handleGasSettingChange = useCallback((option, gasPrice) => {
    setTempGasOption(option);
    setTempGasPrice(gasPrice);
    
    // Update confirmation details with new gas estimate
    if (confirmationDetails) {
      // Determine gas limit based on transaction type
      let gasLimit;
      if (confirmationDetails.type === t('wallet.transactionTypes.nftTransfer', 'NFT Transfer')) {
        gasLimit = 100000; // NFT transfer
      } else if (confirmationDetails.type === t('wallet.transactionTypes.tokenTransfer', 'Token Transfer')) {
        gasLimit = 60000; // ERC-20 transfer
      } else {
        gasLimit = 21000; // Native transfer
      }
      
      let gasPriceBN;
      
      if (option === 'custom') {
        gasPriceBN = parseUnits(String(gasPrice), 'gwei');
      } else {
        const currentGasPrice = dynamicGasPrice ? parseFloat(dynamicGasPrice) : 3;
        let multiplier;
        switch (option) {
          case 'slow': multiplier = 0.8; break;
          case 'medium': multiplier = 1.0; break;
          case 'fast': multiplier = 1.2; break;
          default: multiplier = 1.0;
        }
        const calculatedPrice = (currentGasPrice * multiplier).toFixed(9);
        gasPriceBN = parseUnits(calculatedPrice, 'gwei');
      }
      
      const totalFeeWei = gasPriceBN * BigInt(gasLimit);
      const totalFeeEth = formatEther(totalFeeWei);
      
      // Get USD value if available
      const nativeTokenSymbol = getNativeTokenSymbol();
      
      // Create updated details object with new gas fee
      const updatedDetails = {
        ...confirmationDetails,
        gasFee: `${totalFeeEth} ${nativeTokenSymbol}`
      };
      
      setConfirmationDetails(updatedDetails);
    }
  }, [confirmationDetails, dynamicGasPrice, getNativeTokenSymbol, t]);

  // Get a human-readable version of the gas price
  const getReadableGasPrice = useCallback((specificOption) => {
    const useOption = specificOption || tempGasOption || gasOption;
    const currentGasPrice = dynamicGasPrice ? parseFloat(dynamicGasPrice) : null;
    
    let rawPrice;
    let speedLabel;
    
    if (currentGasPrice) {
      switch (useOption) {
        case 'slow':
          rawPrice = currentGasPrice * 0.8;
          speedLabel = t('settings.gas.speedLabels.slow', 'Slow');
          break;
        case 'medium':
          rawPrice = currentGasPrice;
          speedLabel = t('settings.gas.speedLabels.medium', 'Medium');
          break;
        case 'fast':
          rawPrice = currentGasPrice * 1.2;
          speedLabel = t('settings.gas.speedLabels.fast', 'Fast');
          break;
        case 'custom':
          rawPrice = tempGasPrice || customGasPrice;
          speedLabel = t('settings.gas.options.custom', 'Custom');
          break;
        default:
          rawPrice = tempGasPrice || customGasPrice;
          speedLabel = t('settings.gas.options.custom', 'Custom');
      }
    } else {
      rawPrice = tempGasPrice || customGasPrice || "3";
      speedLabel = t(`settings.gas.options.${useOption}`, useOption.charAt(0).toUpperCase() + useOption.slice(1));
    }
    
    // Format the price for display with our consistent formatting function
    const formattedPrice = formatGasPriceForDisplay(rawPrice);
    
    return {
      price: formattedPrice,
      speed: speedLabel
    };
  }, [tempGasOption, tempGasPrice, gasOption, customGasPrice, dynamicGasPrice, t]);

  return {
    selectedToken,
    setSelectedToken,
    selectedNFT, // NEW: Export NFT state
    setSelectedNFT, // NEW: Export NFT setter
    amountIn,
    setAmountIn,
    confirmationDetails,
    setConfirmationDetails,
    txnHash,
    setTxnHash,
    tempGasOption,
    setTempGasOption,
    tempGasPrice,
    setTempGasPrice,
    dynamicGasPrice,
    prepareTransaction,
    prepareNFTTransaction, // NEW: Export NFT prepare function
    executeTransaction,
    executeNFTTransaction, // NEW: Export NFT execute function
    handleGasSettingChange,
    getReadableGasPrice,
    fetchGasPrice,
    getGasPriceFromSettings, // NEW: Export gas price function
    getNativeTokenSymbol // NEW: Export native token symbol function
  };
};